import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link, useRouter } from "@tanstack/react-router"
import { useState } from "react"
import { formatDate } from "@/date"
import { isUnitStatus, statusLabel, type UnitStatus } from "@/inventory"
import { StatusChangeDialog } from "@/inventory/components/status-change-dialog"
import { UnitStatusBadge } from "@/inventory/components/unit-status-badge"
import { getUnitFn, updateUnitStatusFn } from "@/inventory/rpc"
import { Button } from "@/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card"

export const Route = createFileRoute(
  "/_inventory-layout/inventory/units/$unitId",
)({
  loader: async ({ params }) => {
    const id = Number(params.unitId)
    if (!Number.isFinite(id) || id <= 0) {
      throw new Error("Invalid unit id")
    }
    return getUnitFn({ data: { id } })
  },
  component: UnitDetailPage,
})

interface StatusEvent {
  id: number
  fromStatus: string
  toStatus: string
  note: string | null
  changedByUserId: number
  changedAt: number
}

interface UnitDetail {
  id: number
  serialNumber: string
  status: UnitStatus
  unitCostCents: number | null
  notes: string | null
  createdAt: number
  updatedAt: number
  product: {
    id: number
    name: string
    type: string
    brand: string | null
    model: string | null
  } | null
  carrier: { id: number; name: string } | null
  receiveBatch: {
    id: number
    invoiceNumber: string
    unitCostCents: number | null
    receivedAt: number | null
    vendor: { id: number; name: string } | null
  } | null
  statusEvents: StatusEvent[]
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return "Something went wrong"
}

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return "—"
  return `$${(cents / 100).toFixed(2)}`
}

function formatDateTime(ms: number): string {
  const date = new Date(ms)
  const day = formatDate(date)
  const time = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  }).format(date)
  return `${day} ${time}`
}

function eventStatusLabel(value: string): string {
  return isUnitStatus(value) ? statusLabel(value) : value
}

function UnitDetailPage() {
  const unit = Route.useLoaderData() as UnitDetail
  const queryClient = useQueryClient()
  const router = useRouter()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)

  const updateStatus = useMutation({
    mutationFn: (input: { status: UnitStatus; note: string | null }) =>
      updateUnitStatusFn({
        data: {
          unitId: unit.id,
          status: input.status,
          note: input.note,
        },
      }),
    onSuccess: async () => {
      setDialogError(null)
      setDialogOpen(false)
      await queryClient.invalidateQueries({ queryKey: ["units"] })
      await router.invalidate()
    },
    onError: (error) => setDialogError(getErrorMessage(error)),
  })

  async function handleStatusSubmit(data: {
    status: UnitStatus
    note: string | null
  }) {
    setDialogError(null)
    try {
      await updateStatus.mutateAsync(data)
    } catch {
      // surfaced via onError
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-muted-foreground text-sm">
            <Link
              to="/inventory/units"
              className="text-primary underline-offset-4 hover:underline"
            >
              Units
            </Link>
            <span className="mx-1.5">/</span>
            <span className="font-mono">{unit.serialNumber}</span>
          </p>
          <h1 className="mt-1 font-mono font-semibold text-2xl tracking-tight">
            {unit.serialNumber}
          </h1>
          <div className="mt-2">
            <UnitStatusBadge status={unit.status} />
          </div>
        </div>
        <Button
          type="button"
          onClick={() => {
            setDialogError(null)
            setDialogOpen(true)
          }}
        >
          Change status
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Unit</CardTitle>
            <CardDescription>Core inventory fields</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">Serial</dt>
                <dd className="font-medium font-mono">{unit.serialNumber}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Status</dt>
                <dd className="mt-0.5">
                  <UnitStatusBadge status={unit.status} />
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Product</dt>
                <dd className="font-medium">
                  {unit.product?.name ?? "—"}
                  {unit.product?.type ? (
                    <span className="ml-1 text-muted-foreground capitalize">
                      ({unit.product.type})
                    </span>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Brand / model</dt>
                <dd className="font-medium">
                  {[unit.product?.brand, unit.product?.model]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Carrier</dt>
                <dd className="font-medium">{unit.carrier?.name ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Unit cost</dt>
                <dd className="font-medium tabular-nums">
                  {formatCents(unit.unitCostCents)}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-muted-foreground">Notes</dt>
                <dd className="font-medium">{unit.notes?.trim() || "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Created</dt>
                <dd className="font-medium">
                  {formatDateTime(unit.createdAt)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Updated</dt>
                <dd className="font-medium">
                  {formatDateTime(unit.updatedAt)}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Receive batch</CardTitle>
            <CardDescription>How this unit entered inventory</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">Batch</dt>
                <dd className="font-medium tabular-nums">
                  {unit.receiveBatch ? `#${unit.receiveBatch.id}` : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Vendor</dt>
                <dd className="font-medium">
                  {unit.receiveBatch?.vendor?.name ?? "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Invoice</dt>
                <dd className="font-medium">
                  {unit.receiveBatch?.invoiceNumber ?? "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Batch unit cost</dt>
                <dd className="font-medium tabular-nums">
                  {formatCents(unit.receiveBatch?.unitCostCents)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Received at</dt>
                <dd className="font-medium">
                  {unit.receiveBatch?.receivedAt != null
                    ? formatDateTime(unit.receiveBatch.receivedAt)
                    : "—"}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Status history</CardTitle>
          <CardDescription>
            Changes after the unit was created as available
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(unit.statusEvents ?? []).length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No status changes yet.
            </p>
          ) : (
            <ol className="relative space-y-4 border-border border-l pl-4">
              {unit.statusEvents.map((event) => (
                <li key={event.id} className="relative">
                  <span className="absolute top-1.5 -left-[1.3rem] size-2.5 rounded-full border-2 border-background bg-primary" />
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="font-medium text-sm">
                      {eventStatusLabel(event.fromStatus)}
                      <span className="mx-1.5 text-muted-foreground">→</span>
                      {eventStatusLabel(event.toStatus)}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {formatDateTime(event.changedAt)}
                    </span>
                  </div>
                  {event.note ? (
                    <p className="mt-1 text-muted-foreground text-sm">
                      {event.note}
                    </p>
                  ) : null}
                  <p className="mt-0.5 text-muted-foreground text-xs">
                    User #{event.changedByUserId}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      <StatusChangeDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDialogOpen(false)
            setDialogError(null)
          } else {
            setDialogOpen(true)
          }
        }}
        currentStatus={unit.status}
        isSubmitting={updateStatus.isPending}
        error={dialogError}
        onSubmit={handleStatusSubmit}
      />
    </div>
  )
}
